import { useState } from "react";
import dayjs from "dayjs";
import { Modal } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type {
  LunchConflictResolution,
  LunchMenuConflict,
} from "../../../../api/lunchMenus";
import { useViewport } from "../../../../hooks/useViewport";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  posModalStyles as modalStyles,
} from "../../components/modalPrimitives";
import { DISPLAY_DATE, lunchScheduleText } from "../lunchDraft";

interface LunchOverlapModalProps {
  conflicts: LunchMenuConflict[] | null;
  /** Началната дата на новото меню (ISO) — „Приключи старото" го спира ден преди нея */
  newDateFrom: string;
  saving: boolean;
  onClose: () => void;
  onResolve: (resolution: LunchConflictResolution) => void;
}

/**
 * 409 `lunch_menu_overlap`: в един момент може да е активно само едно обедно
 * меню. Изборът е „Приключи старото" (спира ден преди новото, ако е започнало
 * по-рано; иначе спира веднага) или „Изтрий старото"; после записът се повтаря.
 */
export const LunchOverlapModal = ({
  conflicts,
  newDateFrom,
  saving,
  onClose,
  onResolve,
}: LunchOverlapModalProps) => {
  const { isMobile, isNarrow } = useViewport();
  return (
    <Modal
      open={conflicts != null}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isMobile ? undefined : 540}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      {conflicts && (
        <OverlapChoice
          conflicts={conflicts}
          newDateFrom={newDateFrom}
          saving={saving}
          isNarrow={isNarrow}
          onClose={onClose}
          onResolve={onResolve}
        />
      )}
    </Modal>
  );
};

const OverlapChoice = ({
  conflicts,
  newDateFrom,
  saving,
  isNarrow,
  onClose,
  onResolve,
}: Omit<LunchOverlapModalProps, "conflicts"> & {
  conflicts: LunchMenuConflict[];
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  const finishesNow = conflicts.some((conflict) => conflict.finishMode === "now");
  const [resolution, setResolution] = useState<LunchConflictResolution>("finish");
  const names = conflicts.map((conflict) => conflict.name).join(", ");
  const schedules = conflicts.map((conflict) => lunchScheduleText(t, conflict)).join("; ");
  const newStart = dayjs(newDateFrom);
  const finishDates = {
    endDate: newStart.subtract(1, "day").format(DISPLAY_DATE),
    startDate: newStart.format(DISPLAY_DATE),
  };

  const option = (
    value: LunchConflictResolution,
    title: string,
    hint: string,
  ) => {
    const selected = resolution === value;
    return (
      <button
        type="button"
        onClick={() => setResolution(value)}
        aria-pressed={selected}
        className={`flex w-full cursor-pointer items-start gap-3 rounded-[14px] border-[1.5px] px-3.5 py-3 text-left transition-colors ${
          selected ? "border-pos-accent bg-pos-marked" : "border-pos-line bg-pos-card hover:border-pos-accent/40"
        }`}
      >
        <span
          aria-hidden
          className={`mt-0.5 h-5 w-5 shrink-0 rounded-full ${
            selected ? "border-[6px] border-pos-accent" : "border-[1.5px] border-pos-check"
          }`}
        />
        <span className="flex flex-col gap-0.5">
          <span
            className={`text-[15px] font-bold ${
              value === "delete" ? "text-pos-danger-ink" : "text-pos-ink"
            }`}
          >
            {title}
          </span>
          <span className="text-[13px] leading-[1.45] text-pos-soft2">{hint}</span>
        </span>
      </button>
    );
  };

  return (
    <>
      <ModalHeader
        icon={<WarningOutlined />}
        title={t("lunchMenu.overlapTitle")}
        onClose={onClose}
        isNarrow={isNarrow}
      />
      <div className={`flex flex-col gap-2.5 ${isNarrow ? "px-4 py-4" : "px-[22px] py-5"}`}>
        <p className="m-0 mb-1 text-[13.5px] font-medium leading-[1.55] text-pos-soft">
          {t("lunchMenu.overlapMessage", { name: names, schedule: schedules })}
        </p>
        {option(
          "finish",
          t("lunchMenu.finishOld"),
          finishesNow
            ? t("lunchMenu.finishOldNowHint")
            : t("lunchMenu.finishOldHint", finishDates),
        )}
        {option("delete", t("lunchMenu.deleteOld"), t("lunchMenu.deleteOldHint"))}
      </div>
      <ModalFooter isNarrow={isNarrow}>
        <GhostButton label={t("common.back")} onClick={onClose} />
        <PrimaryButton
          label={t(resolution === "finish" ? "lunchMenu.finishAndSave" : "lunchMenu.deleteAndSave")}
          loading={saving}
          onClick={() => onResolve(resolution)}
        />
      </ModalFooter>
    </>
  );
};
